/**
 * AppLayout -- main layout orchestrating command center + chat.
 *
 * Layout:
 * - Desktop: command center (left, flex-1) + chat panel (right, 400px, collapsible)
 * - Mobile: command center full width (default), chat toggleable via header button
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSubscription } from "@atlas/billing";
import { useChat } from "../../hooks/use-chat";
import type { ToolResult } from "../../hooks/use-chat";
import { useToday } from "../../hooks/use-today";
import { useTasks } from "../../hooks/use-tasks";
import { useEvents } from "../../hooks/use-events";
import { useProfile } from "../../hooks/use-profile";
import { ChatContainer } from "../chat/chat-container";
import { Sidebar } from "./sidebar/sidebar";
import type { SidebarTask, SidebarEvent, TodayPlan } from "./sidebar/types";
import { TaskDetailPopover } from "../task/task-detail-popover";
import { EventDetailPopover } from "../event/event-detail-popover";
import { AppHeader } from "./app-header";
import { SetupWizard } from "../onboarding/setup-wizard";
import { config, billingApiConfig } from "../../config";

/**
 * Safely extract a string from an unknown value
 */
function str(val: unknown): string | undefined {
  return typeof val === "string" ? val : undefined;
}

/**
 * Parse a tool result JSON into a SidebarEvent
 */
function parseEvent(json: string): SidebarEvent | null {
  try {
    const raw = JSON.parse(json) as Record<string, unknown>;
    const id = str(raw.id) ?? str(raw.eventId);
    const title = str(raw.title);
    const startTime = str(raw.startTime);
    if (!id || !title || !startTime) return null;
    return {
      id,
      title,
      startTime,
      endTime: str(raw.endTime),
      type: str(raw.type),
      location: str(raw.location),
      allDay: typeof raw.allDay === "boolean" ? raw.allDay : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Process a tool result and update sidebar state accordingly.
 * Only handles event mutations -- task mutations are now handled via direct CRUD.
 */
function processToolResult(
  result: ToolResult,
  _setTasks: React.Dispatch<React.SetStateAction<SidebarTask[]>>,
  setEvents: React.Dispatch<React.SetStateAction<SidebarEvent[]>>,
  refreshTasks: () => Promise<void>,
): void {
  const { toolName, result: resultJson } = result;

  switch (toolName) {
    // Task mutations via chat still trigger a refresh
    case "create_task":
    case "update_task":
    case "complete_task":
    case "delete_task":
    case "search_tasks":
      refreshTasks();
      break;

    case "create_event": {
      const event = parseEvent(resultJson);
      if (event) {
        setEvents((prev) => [...prev, event]);
      }
      break;
    }

    default:
      break;
  }
}

export function AppLayout(): React.ReactElement {
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<SidebarTask | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<SidebarEvent | null>(null);
  const [sidebarTasks, setSidebarTasks] = useState<SidebarTask[]>([]);
  const [sidebarEvents, setSidebarEvents] = useState<SidebarEvent[]>([]);

  // Onboarding wizard state
  const [showWizard, setShowWizard] = useState(false);
  const wizardShownRef = useRef(false);

  // Subscription hook for token balance refresh
  const { refresh: refreshSubscription } = useSubscription({ apiConfig: billingApiConfig });

  // Profile hook
  const { profile, updateProfile } = useProfile(config.api.baseUrl);

  // Data fetching hooks with mutation methods
  const { plan: todayPlan } = useToday(config.api.baseUrl);
  const {
    tasks: apiTasks,
    refresh: refreshApiTasks,
    createTask,
    updateTask,
    deleteTask,
  } = useTasks(config.api.baseUrl, { includeDone: true });
  const { events: apiEvents, fetchEvents } = useEvents(config.api.baseUrl);

  const { messages, isStreaming, error, lastToolResult, sendMessage, clearChat } =
    useChat(config.chat.url, { onStreamComplete: refreshSubscription });

  // Transform API data to sidebar format
  useEffect(() => {
    if (apiTasks.length > 0) {
      setSidebarTasks(
        apiTasks.map((t) => ({
          id: t.id,
          title: t.title,
          description: t.description,
          priority: t.priority,
          status: t.status,
          dueDate: t.dueDate,
          dueType: t.dueType,
          scheduledDate: t.scheduledDate,
          project: t.projectId,
          tags: t.tags,
          effort: t.effort,
          blockedBy: t.blockedBy,
          aiNotes: t.aiNotes,
          aiPriority: t.aiPriority,
          reschedulingCount: t.reschedulingCount,
          consequenceOfDelay: t.consequenceOfDelay,
        }))
      );
    }
  }, [apiTasks]);

  useEffect(() => {
    if (apiEvents.length > 0) {
      setSidebarEvents(
        apiEvents.map((e) => ({
          id: e.id,
          title: e.title,
          startTime: e.startTime,
          endTime: e.endTime,
          type: e.eventType,
          location: e.location,
          allDay: e.allDay,
        }))
      );
    }
  }, [apiEvents]);

  // Transform todayPlan to sidebar-compatible TodayPlan format
  const sidebarTodayPlan: TodayPlan | undefined = useMemo(() => {
    if (!todayPlan) return undefined;

    const transformPlannedItems = (items: typeof todayPlan.mustDo): SidebarTask[] =>
      items.map((item) => ({
        id: item.task.id,
        title: item.task.title,
        description: item.task.description,
        priority: item.task.priority,
        status: item.task.status,
        dueDate: item.task.dueDate,
        dueType: item.task.dueType,
        scheduledDate: item.task.scheduledDate,
        project: item.task.projectId,
        tags: item.task.tags,
        effort: item.task.effort,
        blockedBy: item.task.blockedBy,
        aiNotes: item.task.aiNotes,
        aiPriority: item.task.aiPriority,
        reschedulingCount: item.task.reschedulingCount,
        consequenceOfDelay: item.task.consequenceOfDelay,
        reasoning: item.reasoning,
        score: item.score,
      }));

    return {
      mustDo: transformPlannedItems(todayPlan.mustDo),
      shouldDo: transformPlannedItems(todayPlan.shouldDo),
      warnings: todayPlan.warnings,
    };
  }, [todayPlan]);

  // Callback for CalendarPanel date range navigation
  const handleCalendarRangeChange = useCallback(
    (start: string, end: string) => {
      fetchEvents(start, end);
    },
    [fetchEvents]
  );

  // Update sidebar from tool_end events (chat-initiated mutations)
  useEffect(() => {
    if (!lastToolResult) return;
    processToolResult(lastToolResult, setSidebarTasks, setSidebarEvents, refreshApiTasks);
  }, [lastToolResult, refreshApiTasks]);

  // Auto-show wizard for new users
  useEffect(() => {
    if (profile && !profile.onboardingComplete && !wizardShownRef.current) {
      wizardShownRef.current = true;
      setShowWizard(true);
    }
  }, [profile]);

  const handleToggleChat = useCallback(() => {
    setChatOpen((prev) => !prev);
  }, []);

  const handleWizardComplete = useCallback(() => {
    setShowWizard(false);
    setChatOpen(true);
    sendMessage("I just finished setting up. What should I focus on first?");
  }, [sendMessage]);

  const handleWizardDismiss = useCallback(() => {
    setShowWizard(false);
    setChatOpen(true);
  }, []);

  const handleTaskClick = useCallback((task: SidebarTask) => {
    setSelectedTask(task);
  }, []);

  const handleEventClick = useCallback((event: SidebarEvent) => {
    setSelectedEvent(event);
  }, []);

  // Direct CRUD task handlers
  const handleCreateTask = useCallback((input: { title: string; priority?: string; dueDate?: string; projectId?: string; consequenceOfDelay?: string }) => {
    createTask(input);
  }, [createTask]);

  const handleUpdateTask = useCallback((id: string, updates: Record<string, unknown>) => {
    updateTask(id, updates);
  }, [updateTask]);

  const handleDeleteTask = useCallback((id: string) => {
    deleteTask(id);
  }, [deleteTask]);

  // For task detail popover actions
  const handleMarkTaskComplete = useCallback((task: SidebarTask) => {
    updateTask(task.id, { status: "done" });
  }, [updateTask]);

  const handleRescheduleTask = useCallback((task: SidebarTask, date: string) => {
    updateTask(task.id, { scheduledDate: date, dueDate: date });
  }, [updateTask]);

  const handleDeleteTaskFromPopover = useCallback((task: SidebarTask) => {
    deleteTask(task.id);
    setSelectedTask(null);
  }, [deleteTask]);

  return (
    <div className="flex h-[100dvh] flex-col">
      {/* Header */}
      <AppHeader
        onToggleChat={handleToggleChat}
        chatOpen={chatOpen}
      />

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Command Center (main area, always visible) */}
        <Sidebar
          tasks={sidebarTasks}
          events={sidebarEvents}
          todayPlan={sidebarTodayPlan}
          projects={profile?.projects}
          profile={profile}
          onTaskClick={handleTaskClick}
          onEventClick={handleEventClick}
          onCalendarRangeChange={handleCalendarRangeChange}
          onOpenChat={() => setChatOpen(true)}
          onOpenWizard={() => setShowWizard(true)}
          onCreateTask={handleCreateTask}
          onUpdateTask={handleUpdateTask}
          onDeleteTask={handleDeleteTask}
        />

        {/* Chat panel (toggleable on all sizes) */}
        <div
          className={[
            "flex flex-col min-w-0 border-l border-border bg-bg",
            // Mobile: overlay when open, hidden when closed
            chatOpen
              ? "fixed inset-0 top-12 z-50 md:static md:z-auto"
              : "hidden md:flex",
            // Desktop: fixed width, collapsible
            chatOpen ? "md:w-[400px] md:flex-none" : "md:w-0 md:overflow-hidden md:border-l-0",
            "transition-all duration-300",
          ].join(" ")}
        >
          {/* Mobile close button for chat */}
          {chatOpen && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-border md:hidden">
              <span className="text-sm font-semibold text-text-primary">AI Assistant</span>
              <button
                type="button"
                onClick={handleToggleChat}
                className="p-1 rounded-md text-text-tertiary hover:text-text-primary hover:bg-surface-hover"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          <ChatContainer
            messages={messages}
            isStreaming={isStreaming}
            error={error}
            sendMessage={sendMessage}
            clearChat={clearChat}
            isOnboarding={!profile?.onboardingComplete}
          />
        </div>
      </div>

      {/* Task detail popover */}
      <TaskDetailPopover
        task={selectedTask}
        isOpen={selectedTask !== null}
        onClose={() => setSelectedTask(null)}
        onMarkComplete={handleMarkTaskComplete}
        onReschedule={handleRescheduleTask}
        onDelete={handleDeleteTaskFromPopover}
        onUpdateTask={handleUpdateTask}
        projects={profile?.projects}
      />

      {/* Event detail popover */}
      <EventDetailPopover
        event={selectedEvent}
        isOpen={selectedEvent !== null}
        onClose={() => setSelectedEvent(null)}
      />

      {/* Setup wizard for new users */}
      <SetupWizard
        isOpen={showWizard}
        profile={profile}
        onUpdateProfile={updateProfile}
        onComplete={handleWizardComplete}
        onDismiss={handleWizardDismiss}
      />
    </div>
  );
}
